import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Music, User, Plus, ExternalLink } from 'lucide-react';

const ExplorePublicPlaylists = ({ user }) => {
  const navigate = useNavigate();
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [cloning, setCloning] = useState(null);

  useEffect(() => {
    fetchPublicPlaylists();
  }, []);

  const fetchPublicPlaylists = async (search = '') => {
    try {
      setLoading(true);
      const url = new URL(`${process.env.REACT_APP_BACKEND_URL}/api/playlists/public/explore`);
      if (search) {
        url.searchParams.append('search', search);
      }
      
      const response = await fetch(url);
      
      if (response.ok) {
        const data = await response.json();
        setPlaylists(data);
      }
    } catch (error) {
      console.error('Failed to fetch public playlists:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchPublicPlaylists(searchQuery);
  };

  const handleClone = async (playlistId, playlistName) => {
    if (!user) {
      alert('Please log in to save playlists');
      navigate('/login');
      return;
    }

    setCloning(playlistId);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/playlists/${playlistId}/clone`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        }
      );

      if (response.ok) {
        const result = await response.json();
        alert(`"${playlistName}" has been added to your library!`);
        
        // Trigger playlist refresh in sidebar
        window.dispatchEvent(new Event('playlistsChanged'));
        
        // Optionally navigate to the cloned playlist
        // navigate(`/playlist/${result.playlist.id}`);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to save playlist');
      }
    } catch (error) {
      console.error('Error cloning playlist:', error);
      alert('Failed to save playlist');
    } finally {
      setCloning(null);
    }
  };

  const handleViewPlaylist = (shareToken) => {
    navigate(`/shared/playlist/${shareToken}`);
  };

  return (
    <div className="page-content" style={{ padding: '40px' }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ 
          fontSize: '48px', 
          fontWeight: 'bold', 
          marginBottom: '12px',
          background: 'linear-gradient(135deg, #9D4EDD 0%, #C77DFF 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
          Explore Playlists
        </h1>
        <p style={{ fontSize: '16px', color: '#999' }}>
          Discover and save playlists created by other users
        </p>
      </div>

      {/* Search Bar */}
      <form onSubmit={handleSearch} style={{ marginBottom: '32px' }}>
        <div style={{
          display: 'flex',
          gap: '12px',
          maxWidth: '600px'
        }}>
          <div style={{
            flex: 1,
            position: 'relative'
          }}>
            <Search 
              size={20} 
              style={{
                position: 'absolute',
                left: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#999'
              }}
            />
            <input
              type="text"
              placeholder="Search playlists..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '14px 14px 14px 48px',
                borderRadius: '24px',
                border: '2px solid #333',
                background: '#1a1a1a',
                color: '#fff',
                fontSize: '15px',
                outline: 'none',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#9D4EDD'}
              onBlur={(e) => e.target.style.borderColor = '#333'}
            />
          </div>
          <button
            type="submit"
            style={{
              padding: '14px 32px',
              borderRadius: '24px',
              border: 'none',
              background: 'linear-gradient(135deg, #9D4EDD 0%, #C77DFF 100%)',
              color: 'white',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'transform 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
            onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            Search
          </button>
        </div>
      </form>

      {/* Loading State */}
      {loading && (
        <div style={{ 
          textAlign: 'center', 
          padding: '60px 20px',
          color: '#999'
        }}>
          <Music size={48} color="#9D4EDD" style={{ marginBottom: '16px' }} />
          <p>Loading playlists...</p>
        </div>
      )}

      {/* Playlists Grid */}
      {!loading && playlists.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: '24px'
        }}>
          {playlists.map(playlist => (
            <div
              key={playlist.id}
              style={{
                background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.8) 0%, rgba(42, 42, 42, 0.6) 100%)',
                borderRadius: '12px',
                padding: '20px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                transition: 'all 0.3s',
                cursor: 'pointer'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(157, 78, 221, 0.3)';
                e.currentTarget.style.borderColor = 'rgba(157, 78, 221, 0.5)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
              }}
            >
              {/* Album Art Preview Grid */}
              <div style={{
                width: '100%',
                aspectRatio: '1',
                borderRadius: '8px',
                background: 'linear-gradient(135deg, #9D4EDD 0%, #C77DFF 100%)',
                marginBottom: '16px',
                overflow: 'hidden',
                display: 'grid',
                gridTemplateColumns: playlist.preview_images.length > 1 ? '1fr 1fr' : '1fr',
                gridTemplateRows: playlist.preview_images.length > 2 ? '1fr 1fr' : '1fr',
                gap: '2px'
              }}>
                {playlist.preview_images.length > 0 ? (
                  playlist.preview_images.map((img, idx) => (
                    <img
                      key={idx}
                      src={img}
                      alt=""
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  ))
                ) : (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Music size={48} color="white" />
                  </div>
                )}
              </div>

              {/* Playlist Info */}
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                marginBottom: '8px',
                color: '#fff',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {playlist.name}
              </h3>

              {playlist.description && (
                <p style={{
                  fontSize: '13px',
                  color: '#999',
                  marginBottom: '12px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical'
                }}>
                  {playlist.description}
                </p>
              )}

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '16px',
                fontSize: '13px',
                color: '#999'
              }}>
                <User size={14} />
                <span>{playlist.creator_name}</span>
                <span>•</span>
                <span>{playlist.song_count} songs</span>
              </div>

              {/* Actions */}
              <div style={{
                display: 'flex',
                gap: '8px'
              }}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewPlaylist(playlist.share_token);
                  }}
                  style={{
                    flex: 1,
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px solid rgba(157, 78, 221, 0.5)',
                    background: 'rgba(157, 78, 221, 0.1)',
                    color: '#9D4EDD',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = 'rgba(157, 78, 221, 0.2)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = 'rgba(157, 78, 221, 0.1)';
                  }}
                >
                  <ExternalLink size={14} />
                  View
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClone(playlist.id, playlist.name);
                  }}
                  disabled={cloning === playlist.id}
                  style={{
                    flex: 1,
                    padding: '10px',
                    borderRadius: '8px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #9D4EDD 0%, #C77DFF 100%)',
                    color: 'white',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: cloning === playlist.id ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    opacity: cloning === playlist.id ? 0.6 : 1,
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => {
                    if (cloning !== playlist.id) {
                      e.currentTarget.style.transform = 'scale(1.05)';
                    }
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  <Plus size={14} />
                  {cloning === playlist.id ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && playlists.length === 0 && (
        <div style={{ 
          textAlign: 'center', 
          padding: '60px 20px',
          color: '#999'
        }}>
          <Music size={64} color="#666" style={{ marginBottom: '16px' }} />
          <h3 style={{ fontSize: '20px', marginBottom: '8px', color: '#fff' }}>
            No playlists found
          </h3>
          <p>
            {searchQuery 
              ? 'Try a different search term' 
              : 'No public playlists available yet'}
          </p>
        </div>
      )}
    </div>
  );
};

export default ExplorePublicPlaylists;
